<template>
    <div class="fileupload">
        <label :for="inputId" class="upload-label">{{ label }}</label>
        <p class="upload-status" :class="localUploadStatus.status">{{ getStatusText() }}</p>
        <div v-if="fileType && fileSpecifications" class="file-specification">
            <p class="spec-title">Рекомендации:</p>
            <p class="spec-details">{{ formatSpecification(getFileSpecification()) }}</p>
        </div>
        <input :id="inputId" type="file" @change="handleFileUpload" :accept="accept" ref="fileInput" class="file-input" />

        <!-- Прогресс-бар -->
        <div v-if="localUploadStatus.status === 'inprogress'" class="progress-container">
            <div class="progress-bar">
                <div class="progress-fill" :style="{ width: localUploadStatus.progress + '%' }"></div>
            </div>
            <span class="progress-text">{{ localUploadStatus.progress }}%</span>
        </div>

        <!-- Отображение UUID после успешной загрузки -->
        <div v-if="localUploadStatus.status === 'ok' && uploadedFileId" class="uploaded-file-info">
            <p class="file-id-label">UUID файла:</p>
            <div class="file-id-container">
                <input
                    type="text"
                    :value="uploadedFileId"
                    readonly
                    class="file-id-input"
                    @click="$event.target.select()"
                />
                <button
                    type="button"
                    @click="copyToClipboard"
                    class="copy-btn"
                    title="Копировать в буфер обмена"
                >
                    Копировать
                </button>
            </div>
        </div>

        <!-- Сообщение об ошибке -->
        <p v-if="localUploadStatus.error" class="error-message">{{ localUploadStatus.error }}</p>

        <ButtonBase v-if="localUploadStatus.status === 'inprogress'" color="gray" @click="cancelUpload">Отменить</ButtonBase>
    </div>
</template>

<script>
import ButtonBase from './ButtonBase';
import { uploadFileWithProgress } from '../services/api';
import { maybeCompressImage } from '../utils/compressToWebp';

export default {
    name: 'FileUpload',
    components: {
        ButtonBase
    },
    props: {
        label: {
            type: String,
            required: true
        },
        accept: {
            type: String,
            default: '*/*'
        },
        onUpload: {
            type: Function,
            required: true
        },
        inputId: {
            type: String,
            default() {
                return 'file-upload-' + Math.random().toString(36).substr(2, 9);
            }
        },
        fileType: {
            type: String,
            default: null,
            validator: value => ['cover', 'short_video', 'main_video', 'backstage_image', 'backstage_video', null].includes(value)
        }
    },
    data() {
        return {
            localUploadStatus: {
                progress: 0,
                status: 'idle',
                error: null
            },
            currentFile: null,
            uploadAbortController: null,
            uploadedFileId: null,
            fileSpecifications: null
        };
    },
    async mounted() {
        if (this.fileType) {
            await this.loadFileSpecifications();
        }
    },
    methods: {
        async loadFileSpecifications() {
            try {
                // Импортируем функцию динамически, чтобы избежать циклических зависимостей
                const { getFileSpecifications } = await import('../services/api');
                this.fileSpecifications = await getFileSpecifications();
            } catch (error) {
                console.error('Ошибка загрузки рекомендаций по файлам:', error);
            }
        },

        async handleFileUpload(event) {
            const selectedFile = event.target.files[0];
            if (!selectedFile) return;

            this.uploadedFileId = null; // Сбрасываем UUID при новой загрузке
            this.localUploadStatus.status = 'inprogress';
            this.localUploadStatus.progress = 0;
            this.localUploadStatus.error = null;

            // Изображения пережимаем в WebP на клиенте перед загрузкой.
            // Видео и прочие файлы проходят без изменений.
            const file = await maybeCompressImage(selectedFile);
            this.currentFile = file;

            try {
                // Используем новый API с прогресс-баром
                const response = await uploadFileWithProgress(
                    file,
                    (progress) => {
                        this.localUploadStatus.progress = progress;
                    },
                    (error) => {
                        console.error('Ошибка загрузки:', error);
                        this.localUploadStatus.error = error.message;
                    }
                );

                this.localUploadStatus.status = 'ok';
                this.uploadedFileId = response.id; // Сохраняем UUID загруженного файла

                this.$emit('file-uploaded', {
                    id: response.id,
                    status: this.localUploadStatus.status,
                    file: response
                });

                // Также вызываем prop callback если он есть
                if (this.onUpload) {
                    await this.onUpload(file, (progress) => {
                        this.localUploadStatus.progress = progress;
                    });
                }

            } catch (error) {
                this.localUploadStatus.status = 'error';
                this.localUploadStatus.error = error.message || 'Ошибка загрузки файла';
                this.$emit('file-uploaded', {
                    id: null,
                    status: this.localUploadStatus.status,
                    error: this.localUploadStatus.error
                });
            }
        },

        cancelUpload() {
            this.localUploadStatus.status = 'idle';
            this.localUploadStatus.progress = 0;
            this.localUploadStatus.error = null;
            this.currentFile = null;
            this.uploadedFileId = null;

            // Очищаем input
            if (this.$refs.fileInput) {
                this.$refs.fileInput.value = '';
            }

            // Вызываем callback с null если он есть
            if (this.onUpload) {
                this.onUpload(null);
            }
        },

        copyToClipboard() {
            if (this.uploadedFileId) {
                navigator.clipboard.writeText(this.uploadedFileId).then(() => {
                    alert('UUID скопирован в буфер обмена');
                }).catch(err => {
                    console.error('Ошибка копирования в буфер обмена:', err);
                });
            }
        },

        getStatusText() {
            switch (this.localUploadStatus.status) {
                case 'idle':
                    return 'Готов к загрузке';
                case 'inprogress':
                    return `Загрузка... ${this.localUploadStatus.progress}%`;
                case 'ok':
                    return 'Файл успешно загружен';
                case 'error':
                    return 'Ошибка загрузки';
                default:
                    return '';
            }
        },

        getFileSpecification() {
            if (!this.fileType || !this.fileSpecifications) return null;
            return this.fileSpecifications[this.fileType] || null;
        },

        formatSpecification(spec) {
            if (!spec) return '';
            return Object.entries(spec)
                .filter(([key]) => key !== 'description')
                .map(([key, value]) => `${key}: ${value}`)
                .join(', ');
        }
    }
}
</script>

<style scoped>
.fileupload {
    margin: 12px 0;
    padding: 16px;
    border-radius: 12px;
    border: 1px solid hsl(var(--border));
    background: hsl(var(--card));
    width: 100%;
    max-width: 720px;
}

.upload-label {
    display: block;
    margin-bottom: 8px;
    font-size: 14px;
    font-weight: 600;
    color: hsl(var(--foreground));
}

.upload-status {
    margin: 8px 0;
    font-size: 13px;
    font-weight: 500;
}

.upload-status.idle {
    color: hsl(var(--muted-foreground));
}

.upload-status.inprogress {
    color: hsl(var(--accent));
}

.upload-status.ok {
    color: hsl(var(--success));
}

.upload-status.error {
    color: hsl(var(--destructive));
}

.progress-container {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 15px 0;
}

.progress-bar {
    flex: 1;
    height: 8px;
    background: hsl(var(--muted));
    border-radius: 4px;
    overflow: hidden;
}

.progress-fill {
    height: 100%;
    background: linear-gradient(90deg, hsl(var(--accent)), hsl(var(--primary-hover)));
    border-radius: 4px;
    transition: width 0.3s ease;
}

.progress-text {
    font-size: 12px;
    color: hsl(var(--muted-foreground));
    font-weight: 500;
    min-width: 40px;
    text-align: right;
}

.error-message {
    color: hsl(var(--destructive));
    font-size: 14px;
    margin-top: 10px;
    padding: 8px;
    background: hsl(var(--destructive) / 0.12);
    border: 1px solid hsl(var(--destructive) / 0.35);
    border-radius: 8px;
}

.file-input {
    width: 100%;
    padding: 9px 10px;
    border: 1px solid hsl(var(--input));
    border-radius: 8px;
    background: hsl(var(--background));
    color: hsl(var(--foreground));
    font-size: 13px;
    margin: 10px 0;
    transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.file-input:focus {
    outline: none;
    border-color: hsl(var(--ring));
    box-shadow: 0 0 0 2px hsl(var(--ring) / 0.25);
}

.file-input::file-selector-button {
    margin-right: 10px;
    border: 1px solid hsl(var(--border));
    border-radius: 6px;
    background: hsl(var(--muted));
    color: hsl(var(--foreground));
    padding: 5px 10px;
    font-size: 12px;
    font-weight: 600;
    cursor: pointer;
    transition: background-color 0.2s ease;
}

.file-input::file-selector-button:hover {
    background: hsl(var(--secondary));
}

.uploaded-file-info {
    margin-top: 12px;
    padding: 10px;
    background: hsl(var(--accent) / 0.1);
    border: 1px solid hsl(var(--accent) / 0.35);
    border-radius: 8px;
}

.file-id-label {
    margin: 0 0 8px 0;
    color: hsl(var(--foreground));
    font-size: 14px;
    font-weight: 500;
}

.file-id-container {
    display: flex;
    gap: 8px;
    align-items: center;
}

.file-id-input {
    flex: 1;
    padding: 8px;
    border: 1px solid hsl(var(--input));
    border-radius: 8px;
    background: hsl(var(--background));
    color: hsl(var(--foreground));
    font-size: 12px;
    font-family: monospace;
    cursor: text;
}

.file-id-input:focus {
    outline: none;
    border-color: hsl(var(--ring));
    box-shadow: 0 0 0 2px hsl(var(--ring) / 0.25);
}

.copy-btn {
    padding: 8px 12px;
    border: 1px solid hsl(var(--accent));
    border-radius: 8px;
    background: hsl(var(--accent));
    color: hsl(var(--accent-foreground));
    cursor: pointer;
    font-size: 12px;
    font-weight: 600;
    transition: background-color 0.2s, border-color 0.2s;
}

.copy-btn:hover {
    background: hsl(var(--primary-hover));
    border-color: hsl(var(--primary-hover));
}

.copy-btn:active {
    transform: translateY(1px);
}

/* Стили для рекомендаций */
.file-specification {
    margin: 10px 0;
    padding: 10px;
    background: hsl(var(--muted));
    border: 1px solid hsl(var(--border));
    border-radius: 8px;
    font-size: 12px;
}

.spec-title {
    margin: 0 0 5px 0;
    color: hsl(var(--foreground));
    font-weight: 500;
    font-size: 13px;
}

.spec-details {
    margin: 0;
    color: hsl(var(--muted-foreground));
}
</style>
